import { Fragment } from "react";
import { Check, Minus } from "lucide-react";
import { motion } from "framer-motion";
import { reveal } from "@/components/landing/motion";
import { Button } from "@/components/ui/button";
import { monthlyPlans, dailyPlans, compareRows } from "@/lib/landingData";

export const PricingV2 = ({ goStart }) => (
  <section id="prezzi" className="py-28 bg-[#FAFBFA] border-t border-gray-100">
    <div className="max-w-7xl mx-auto px-6">
      <motion.div {...reveal()} className="max-w-2xl mb-16">
        <p className="text-[#5aa469] text-xs tracking-[0.25em] uppercase mb-4">Prezzi</p>
        <h2 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight">Prezzi chiari, si parte gratis</h2>
        <p className="text-gray-500 mt-4 text-lg">Nessuna commissione sugli ordini. 14 giorni di prova gratuita sui piani mensili, poi scegli tu: piano Free o a pagamento · Prezzi IVA inclusa.</p>
      </motion.div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 items-stretch">
        {monthlyPlans.map((p, i) => (
          <motion.div key={p.key} {...reveal(i * 0.08)} className={`relative rounded-[1.75rem] p-7 flex flex-col border transition-[transform,border-color,box-shadow] hover:-translate-y-1 ${p.highlight ? "bg-gray-900 text-white border-gray-900 shadow-2xl shadow-[#5aa469]/25 lg:-my-4" : "bg-white border-gray-200 hover:border-[#5aa469]/50"}`} data-testid={`plan-${p.key}`}>
            {p.highlight && <span className="absolute -top-3 left-7 text-[11px] font-semibold tracking-wide uppercase text-white bg-[#5aa469] px-3 py-1 rounded-full">Più scelto</span>}
            <h3 className="font-display font-bold text-2xl mt-1">{p.name}</h3>
            <div className="mt-4 flex items-baseline gap-1">
              <span className="text-5xl font-extrabold font-display tracking-tight">{p.price}<span className="text-2xl align-top ml-0.5">€</span></span>
              <span className={p.highlight ? "text-gray-400" : "text-gray-500"}>/{p.period}</span>
            </div>
            <p className={`mt-2 mb-6 text-xs font-medium ${p.highlight ? "text-[#8fd39c]" : "text-[#4a8a56]"}`} data-testid={`trial-note-${p.key}`}>{p.free ? "Gratis per sempre, nessuna carta richiesta" : `Prova gratis per 14 giorni, poi ${p.price} €/mese`}</p>
            <ul className="space-y-2.5 mb-8 flex-1">
              {p.features.map((f) => (
                <li key={f} className={`flex items-start gap-2 text-sm ${p.highlight ? "text-gray-200" : "text-gray-700"}`}>
                  <Check className="h-4 w-4 text-[#5aa469] mt-0.5 shrink-0" /> {f}
                </li>
              ))}
            </ul>
            <Button onClick={goStart} className={`rounded-full w-full h-11 active:scale-95 transition-transform ${p.highlight ? "bg-[#5aa469] hover:bg-[#4a8a56] text-white font-semibold" : "bg-gray-900 hover:bg-gray-800 text-white font-medium"}`} data-testid={`plan-cta-${p.key}`}>{p.free ? "Inizia gratis" : "Inizia la prova gratis"}</Button>
          </motion.div>
        ))}
      </div>

      <div className="mt-16 grid lg:grid-cols-[1fr_2fr] gap-8 items-start">
        <div>
          <h3 className="font-display font-semibold text-3xl">Piani a tempo (24h)</h3>
        </div>
        {dailyPlans.map((p) => (
          <div key={p.key} className="rounded-[1.75rem] bg-white border border-gray-200 p-7 grid sm:grid-cols-[auto_1fr_auto] gap-6 items-center" data-testid={`plan-${p.key}`}>
            <div>
              <h4 className="font-display font-semibold text-2xl">{p.name}</h4>
              <span className="text-3xl font-extrabold font-display">{p.price} €<span className="text-sm font-normal text-gray-500">/giorno</span></span>
            </div>
            <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5">
              {p.features.map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm text-gray-600"><Check className="h-3.5 w-3.5 text-[#5aa469] mt-0.5 shrink-0" /> {f}</li>
              ))}
            </ul>
            <Button onClick={goStart} className="rounded-full bg-gray-900 hover:bg-gray-800 text-white px-6" data-testid={`plan-cta-${p.key}`}>Acquista</Button>
          </div>
        ))}
      </div>
    </div>
  </section>
);

export const CompareV2 = ({ goStart }) => (
  <section id="confronta" className="py-28 bg-white border-t border-gray-100">
    <div className="max-w-6xl mx-auto px-6">
      <div className="max-w-2xl mb-12">
        <p className="text-[#5aa469] text-xs tracking-[0.25em] uppercase mb-4">Confronta</p>
        <h2 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight">Confronta tutti i piani</h2>
        <p className="text-gray-500 mt-4 text-lg">Trova a colpo d'occhio il piano perfetto per la tua attività.</p>
      </div>
      <motion.div {...reveal()} className="overflow-x-auto rounded-[1.75rem] border border-gray-200" data-testid="compare-table">
        <table className="w-full min-w-[760px] text-sm">
          <thead>
            <tr className="border-b border-gray-200 bg-[#FAFBFA]">
              <th className="text-left font-medium text-gray-500 p-4 sticky left-0 bg-[#FAFBFA] z-10">Funzionalità</th>
              {monthlyPlans.map((p) => (
                <th key={p.key} className={`p-4 text-center ${p.highlight ? "bg-[#5aa469]/10" : ""}`}>
                  <div className="font-display font-bold text-lg text-gray-900">{p.name}</div>
                  <div className="text-gray-500 text-xs">{p.free ? "Gratis" : `${p.price} €/mese`}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {compareRows.map((row) => (
              <Fragment key={row.label}>
                {row.group && (
                  <tr className="bg-[#FAFBFA]" data-testid={`compare-group-${row.group}`}>
                    <td colSpan={monthlyPlans.length + 1} className="px-4 pt-5 pb-2 text-[11px] font-semibold tracking-[0.2em] uppercase text-[#4a8a56] sticky left-0">{row.group}</td>
                  </tr>
                )}
                <tr className="border-b border-gray-100 hover:bg-[#FAFBFA] transition-colors">
                <td className="p-4 text-gray-700 sticky left-0 bg-white z-10">{row.label}</td>
                {row.values.map((v, ci) => (
                  <td key={monthlyPlans[ci].key} className={`p-4 text-center ${monthlyPlans[ci].highlight ? "bg-[#5aa469]/5" : ""}`}>
                    {v === true ? <span className="inline-flex h-6 w-6 rounded-full bg-[#5aa469]/12 items-center justify-center"><Check className="h-3.5 w-3.5 text-[#4a8a56]" /></span> : v === false ? <Minus className="h-4 w-4 text-gray-300 mx-auto" /> : <span className="font-semibold text-gray-900">{v}</span>}
                  </td>
                ))}
                </tr>
              </Fragment>
            ))}
            <tr>
              <td className="p-4 sticky left-0 bg-white z-10" />
              {monthlyPlans.map((p) => (
                <td key={p.key} className={`p-4 text-center ${p.highlight ? "bg-[#5aa469]/5" : ""}`}>
                  <Button onClick={goStart} className={`rounded-full h-9 px-5 ${p.highlight ? "bg-[#5aa469] hover:bg-[#4a8a56] text-white font-semibold" : "bg-gray-900 hover:bg-gray-800 text-white"}`} data-testid={`compare-cta-${p.key}`}>{p.free ? "Inizia gratis" : "Acquista"}</Button>
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </motion.div>
      <p className="text-xs text-gray-400 mt-4">Disponibile anche il piano a tempo: A Tempo (1,99 €/giorno). Tutti i prezzi sono IVA inclusa.</p>
    </div>
  </section>
);
